import { useState, useEffect } from "react";
import { useHistory } from "react-router";
import { common_utils } from "../../../../../../../common/common_utils";
import { EventCreationConfirmation } from "../../../../confirmation_page";
import { DashboardUtils } from "../../../../dashboard_utils";
import { EventCreationPropsWithHistory } from "../../../../event_creation";
import { DashboardContainer } from "../../base_components/dashboard_container";
import { SeshAddonInput } from "../../base_components/sesh_addon_input";
import { useDocTitle } from "../../hooks/use_doc_title";
import { useCurrentGuildData } from "../../hooks/use_guild_data";
import { useSearchParams } from "../../hooks/use_search_params";
import {
	CalendarPage,
	DashboardCalendar,
} from "./calendar/components/calendar_page";
import EventListingForm from "./event_listing_form/event_listing_form";
import {
	useCalendarSearchParams,
	ViewValues,
	ViewValuesType,
} from "./hooks/use_calendar_search_params";

type RedirectState = {
	type: "Edit" | "Create";
	message_id: string;
	link?: string;
};

export function redirectToEventListFromCreatePage(
	props: EventCreationPropsWithHistory,
	message_id: string,
	start_time: Date,
	link?: string
) {
	const state: RedirectState = {
		type: props.is_edit ? "Edit" : "Create",
		message_id,
		link,
	};
	

	const url = `${common_utils.getEventListURL(
		props.guild_id
	)}?view=calendar&date=${start_time.getFullYear()}-${
		start_time.getMonth() + 1
	}`;

	props.history.push(url, state);
}

function getRedirectState(history: ReturnType<typeof useHistory>) {
	const state = history.location.state as RedirectState;

	if (!state) {
		return null;
	}

	return state;
}

export function EventListBase() {
	useDocTitle("Events");

	const search_params = useCalendarSearchParams();
	const history = useHistory();

	const [redirect_state, setRedirectState] = useState(
		getRedirectState(history)
	);

	const guild_data = useCurrentGuildData();

	const value = search_params.get("view");

	useEffect(() => {
		if (!ViewValues.includes(value)) {
			search_params.set("view", "calendar");
			history.replace(search_params.toURL());
		}
	}, [value, search_params, history]);

	if (!ViewValues.includes(value)) {
		return null;
	}

	const ViewToggler = (
		<SeshAddonInput
			value={value}
			setValue={(new_value: ViewValuesType) => {
				search_params.set("view", new_value);
				history.replace(search_params.toURL());
			}}
			buttons={[
				{
					label: "Calendar",
					value: "calendar",
				},
				{
					label: "List",
					value: "list",
				},
			]}
		/>
	);

	return (
		<DashboardContainer
			size="xl"
			bread_crumbs={
				<DashboardUtils.RenderLinkChain
					link_chain_data={[
						DashboardUtils.GetGuildSelectionLinkChainData(),
						DashboardUtils.RenderGuildOptionSelectLinkChain(
							guild_data
						),
						DashboardUtils.RenderEventSelectionLinkChain(
							guild_data
						),
					]}
				/>
			}
		>
			{value === "list" ? (
				<EventListingForm viewToggler={ViewToggler} />
			) : (
				<DashboardCalendar viewToggler={ViewToggler} />
			)}
			{redirect_state && (
				<EventCreationConfirmation
					is_edit={redirect_state?.type === "Edit"}
					link={redirect_state?.link}
					onClose={() => setRedirectState(null)}
				/>
			)}
		</DashboardContainer>
	);
}
